// ─────────────────────────────────────────────────────────────────────────────
// Eligibles view — display filters (step-1 filter bar; spec table B / G2-8).
//
// A PURE post-fetch filter over a computed EligiblesView: it narrows the two tabs
// by the table-B criteria that operate on already-fetched, already-eligible rows
// (contractor, Area Name, SSV/TSA Done, SSV/TSA Validation, and a specific upload
// DAY within the month). It does NOT touch the invoicing MONTH — that is the
// period (from/to); a change there re-fetches upstream rather than filtering here.
//
// Tab-scoped by design: the SSV-only criteria (ssvDone / ssvValidation / ssvDate)
// apply to the SSV tab, the TSA-only ones to the TSA tab; contractor + area apply
// to both. This matches the spec's per-tab filter set — the lone "SSV Validation on
// the TSA tab" entry in table B is read as a transcription artifact: each tab
// filters its OWN validation status (Jeremy, 2026-06-29).
//
// Lives in `shared` (not frontend-local) so the SAME predicate drives the Eligibles
// screen (client-side, instant — the month's view is already fetched) AND, later,
// the server-side Export / Validation filtering — one implementation, no desync.
// ─────────────────────────────────────────────────────────────────────────────
const norm = (v) => (v ?? '').trim().toLowerCase();
/** A filter value is "active" (i.e. actually narrows) only when non-empty. */
function active(v) {
    return typeof v === 'string' && v.trim() !== '';
}
/** Trimmed, case-insensitive equality of a row value against a filter value. */
function matches(rowValue, filterValue) {
    if (!active(filterValue))
        return true; // no filter → pass
    return norm(rowValue) === norm(filterValue);
}
/** Exact calendar-DAY (`YYYY-MM-DD`) match, comparing only the leading date. */
function matchesDay(rowDate, filterDay) {
    if (!active(filterDay))
        return true;
    return (rowDate ?? '').slice(0, 10) === filterDay.trim();
}
/** The row's resolved area label — the joined name, falling back to the raw id. */
function areaLabel(r) {
    return r.source.areaName ?? r.source.areaId ?? null;
}
/** Submission filter: 'submitted' keeps submitted rows, 'pending' the rest; else pass. */
function matchesSubmission(r, filter) {
    if (!active(filter))
        return true;
    const submitted = r.submission != null;
    const want = filter.trim().toLowerCase();
    if (want === 'submitted')
        return submitted;
    if (want === 'pending')
        return !submitted;
    return true; // unknown value → no filter
}
/** Imported-file filter: keep rows whose decision was last written by THAT import batch. */
function matchesImportFile(r, filter) {
    if (!active(filter))
        return true;
    return (r.decision?.importBatchId ?? '') === filter.trim();
}
/** A manually- / imported-added ADD row (no vwMDU source → Area/Date/Status fields are
 *  null). It can never satisfy those filters, so it is EXEMPT from them and always shown
 *  (Jeremy 2026-07-08): an explicit add must not be hidden behind a filter it structurally
 *  cannot match. The contractor + submission + import-file filters still apply (real fields). */
function isAddRow(r) {
    const o = r.decision?.rowOrigin;
    return o === 'MANUAL_ADDED' || o === 'IMPORT_ADDED';
}
/** Filters that apply to EVERY row, including an ADD (its contractor + decision are real). */
function keepCommon(r, f) {
    return (matches(r.source.ssvContractor, f.contractor) &&
        matchesSubmission(r, f.submission) &&
        matchesImportFile(r, f.importFile));
}
function keepSsv(r, f) {
    if (!keepCommon(r, f))
        return false;
    if (isAddRow(r))
        return true; // exempt from Area/Date/Status (null on an ADD row)
    return (matches(areaLabel(r), f.area) &&
        matches(r.source.ssvDone, f.ssvDone) &&
        matches(r.source.ssvValidationStatus, f.ssvValidation) &&
        matchesDay(r.source.ssvDate, f.ssvDate));
}
function keepTsa(r, f) {
    if (!keepCommon(r, f))
        return false;
    if (isAddRow(r))
        return true; // exempt from Area/Date/Status (null on an ADD row)
    return (matches(areaLabel(r), f.area) &&
        matches(r.source.tsaDone, f.tsaDone) &&
        matches(r.source.tsaValidationStatus, f.tsaValidation) &&
        matchesDay(r.source.tsaDate, f.tsaDate));
}
/**
 * Filter a computed eligibles view by the table-B display criteria. Returns a NEW
 * view (the input is never mutated): each tab narrowed to the rows that pass its
 * applicable filters, with the per-tab counts recomputed to match —
 *   - `ssv/tsaEligible` = the filtered tab lengths;
 *   - `ssv/tsaUnpriced` = the non-PRICED rows within each filtered tab.
 * `totalFetched` is PASSED THROUGH unchanged: it is a fetch metric (rows read from
 * the source for the period), and a display filter does not change what was
 * fetched — keeping its meaning consistent with buildEligiblesView / applyWorkingSet
 * (the "how many are shown" notion is the per-tab `*Eligible` counts). `period` is
 * likewise unchanged (the upload-month is the period, not a display filter).
 */
export function filterEligiblesView(view, filters) {
    const ssv = view.ssv.filter((r) => keepSsv(r, filters));
    const tsa = view.tsa.filter((r) => keepTsa(r, filters));
    const unpriced = (rows) => rows.reduce((n, r) => (r.tariff.status !== 'PRICED' ? n + 1 : n), 0);
    return {
        period: view.period,
        ssv,
        tsa,
        summary: {
            totalFetched: view.summary.totalFetched,
            ssvEligible: ssv.length,
            tsaEligible: tsa.length,
            ssvUnpriced: unpriced(ssv),
            tsaUnpriced: unpriced(tsa),
        },
    };
}
//# sourceMappingURL=filter.js.map